import React from "react";
import { Link } from "react-router-dom";
import { Calculator, Ship, Scale, ArrowRight } from "lucide-react";

const TOOLS = [
  {
    icon: Scale,
    title: "Vehicle Opportunity Analysis",
    desc: "Enter a vehicle and a source → destination corridor. Atlas computes landed cost, destination value, expected margin, risk and a clear BUY / NEGOTIATE / PASS decision with the maximum price you should pay.",
    to: "/intelligence/new",
    cta: "Open the analysis",
    available: true,
  },
  {
    icon: Calculator,
    title: "Total Cost Calculator",
    desc: "A standalone calculator for the real landed cost of a vehicle — purchase, transport, taxes, registration, inspection and repairs — in a single currency.",
    available: false,
  },
  {
    icon: Ship,
    title: "Import Cost Calculator",
    desc: "Estimate the cross-border cost of importing a vehicle between two European markets, including VAT treatment, registration and duties.",
    available: false,
  },
];

export default function ToolsHub() {
  return (
    <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-12" data-testid="tools-hub">
      <div className="mb-10">
        <span className="text-[11px] uppercase tracking-widest text-slate-400 font-mono">Atlas · Tools</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-atlas-ink mt-1">Acquisition tools</h1>
        <p className="text-slate-500 mt-2 max-w-2xl">
          Practical tools for professional vehicle buyers. Know what to buy, where, and what to pay.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5" data-testid="tools-grid">
        {TOOLS.map(({ icon: Icon, title, desc, to, cta, available }) => (
          <div
            key={title}
            data-testid={`tool-${title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
            className={`rounded-xl border p-6 flex flex-col ${available ? "border-slate-200 bg-white hover:border-atlas-blue" : "border-slate-200 bg-slate-50"} transition-colors`}
          >
            <div className={`h-11 w-11 rounded-md flex items-center justify-center mb-4 ${available ? "bg-atlas-blue/10" : "bg-slate-200"}`}>
              <Icon className={`h-5 w-5 ${available ? "text-atlas-blue" : "text-slate-400"}`} strokeWidth={1.7} />
            </div>
            <div className="flex items-center gap-2">
              <h3 className="font-display text-lg font-semibold text-atlas-ink">{title}</h3>
              {!available && (
                <span className="text-[10px] uppercase tracking-wide font-mono text-slate-500 bg-slate-200 rounded px-1.5 py-0.5">Coming soon</span>
              )}
            </div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed flex-1">{desc}</p>
            {available ? (
              <Link
                to={to}
                data-testid="tool-cta-opportunity"
                className="inline-flex items-center gap-1.5 mt-5 text-atlas-blue font-medium hover:gap-2.5 transition-all"
              >
                {cta} <ArrowRight className="h-4 w-4" />
              </Link>
            ) : (
              <span className="mt-5 text-sm text-slate-400">Available in an upcoming release</span>
            )}
          </div>
        ))}
      </div>

      <p className="text-[11px] text-slate-400 mt-8">
        Atlas provides acquisition intelligence, not the transaction. Figures are calculated from operator-entered data and real ECB FX rates.
      </p>
    </div>
  );
}
